import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import {
    BsBarChart, BsCardImage, BsFileText, BsGraphUp, BsInfoCircle, BsPalette, BsSearch,
} from "react-icons/bs";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";
import { Skeleton } from "../../Components/Needed";

const SITE_IMAGE_TYPES = ["main", "exterior", "interior", "engine", "gallery", "other"];

const TABS = [
    { key: "seo", name: "SEO", icon: <BsSearch /> },
    { key: "overview", name: "Overview", icon: <BsInfoCircle /> },
    { key: "specifications", name: "Specifications", icon: <BsBarChart /> },
    { key: "performance", name: "Performance", icon: <BsGraphUp /> },
    { key: "colors", name: "Colors", icon: <BsPalette /> },
    { key: "images", name: "Images", icon: <BsCardImage /> },
    { key: "content", name: "Content Sections", icon: <BsFileText /> },
];

const emptySeo = { title: "", description: "", keywords: [], canonicalUrl: "", ogTitle: "", ogDescription: "", ogImage: "", robots: "index, follow" };
const emptyOverview = { shortDescription: "", description: "", highlights: [] };
const emptySpecifications = {
    engine: "", engineDisplacement: "", engineType: "", cylinders: "", maxPower: "", maxTorque: "",
    transmission: "", driveType: "", fuelType: "", mileage: "", electricRange: "", batteryCapacity: "",
    chargingTime: "", seatingCapacity: "", bootSpace: "", fuelTankCapacity: "", groundClearance: "",
    length: "", width: "", height: "", wheelbase: "", kerbWeight: "",
};
const emptyPerformance = { topSpeed: "", acceleration: "", zeroToHundred: "", drivingModes: [] };
const emptyColorRow = () => ({ name: "", hex: "", image: "" });
const emptyImageRow = () => ({ url: "", alt: "", title: "", type: SITE_IMAGE_TYPES[0] });
const emptyContentRow = () => ({ heading: "", slug: "", content: "", order: "" });

const ChipListEditor = ({ label, values, onChange, placeholder }) => {
    const [draft, setDraft] = useState("");
    const add = () => {
        const value = draft.trim();
        if (value && !values.includes(value)) onChange([...values, value]);
        setDraft("");
    };
    const remove = (value) => onChange(values.filter((v) => v !== value));
    const onKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add();
        }
    };
    return (
        <div className="col-12">
            <label className="form-label">{label}</label>
            <div className="d-flex flex-wrap gap-2 mb-2">
                {values.map((v) => (
                    <span key={v} className="badge rounded-pill text-bg-light border d-inline-flex align-items-center gap-1">
                        {v}
                        <button type="button" className="btn-close" style={{ fontSize: "0.55rem" }} aria-label={`Remove ${v}`} onClick={() => remove(v)}></button>
                    </span>
                ))}
            </div>
            <div className="input-group">
                <input type="text" className="form-control" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown} placeholder={placeholder} />
                <button type="button" className="btn btn-outline-secondary" onClick={add}>Add</button>
            </div>
        </div>
    );
};

const ModelSiteDataPage = () => {
    const { hasPermission } = useAuth();
    // This page is reachable by URL, not just from the gated "Manage site data"
    // button on the model detail page, so every save control is gated here too.
    // The backend route carries the same key.
    const canSave = hasPermission("vehicle.model.siteData");
    const { modelId } = useParams();
    const navigate = useNavigate();
    const [modelName, setModelName] = useState("");
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("seo");
    const [saving, setSaving] = useState(false);
    const [seo, setSeo] = useState(emptySeo);
    const [overview, setOverview] = useState(emptyOverview);
    const [specifications, setSpecifications] = useState(emptySpecifications);
    const [performance, setPerformance] = useState(emptyPerformance);
    const [colors, setColors] = useState([emptyColorRow()]);
    const [images, setImages] = useState([emptyImageRow()]);
    const [contentSections, setContentSections] = useState([emptyContentRow()]);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: `/staff/vehicle/model/${modelId}` });
        const model = response?.success ? response?.data?.data : null;
        if (model) {
            setModelName(model.modelName || "");
            const sd = model.siteData || {};
            setSeo({ ...emptySeo, ...sd.seo });
            setOverview({ ...emptyOverview, ...sd.overview, highlights: sd.overview?.highlights || [] });
            setSpecifications({ ...emptySpecifications, ...sd.specifications });
            setPerformance({ ...emptyPerformance, ...sd.performance, drivingModes: sd.performance?.drivingModes || [] });
            setColors(sd.colors?.length ? sd.colors.map((c) => ({ name: c.name || "", hex: c.hex || "", image: c.image || "" })) : [emptyColorRow()]);
            setImages(sd.images?.length ? sd.images.map((i) => ({ url: i.url || "", alt: i.alt || "", title: i.title || "", type: i.type || SITE_IMAGE_TYPES[0] })) : [emptyImageRow()]);
            setContentSections(sd.contentSections?.length ? sd.contentSections.map((c) => ({ heading: c.heading || "", slug: c.slug || "", content: c.content || "", order: c.order ?? "" })) : [emptyContentRow()]);
        }
        setLoading(false);
    }, [modelId]);

    useEffect(() => { load(); }, [load]);

    const save = async (section, payload, successMessage) => {
        setSaving(true);
        const response = await Handler({ method: "patch", url: `/staff/vehicle/model/${modelId}/site-data`, data: { [section]: payload } });
        if (response?.success) {
            toast.success(response?.data?.message || successMessage);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save"));
        }
        setSaving(false);
    };

    const saveSeo = () => save("seo", seo, "SEO details saved");

    const saveOverview = () => save("overview", overview, "Overview saved");

    const saveSpecifications = () => {
        const numericFields = ["cylinders", "seatingCapacity"];
        const cleaned = { ...specifications };
        numericFields.forEach((field) => {
            const trimmed = String(cleaned[field]).trim();
            cleaned[field] = trimmed === "" ? undefined : Number(trimmed);
        });
        Object.keys(cleaned).forEach((key) => {
            if (cleaned[key] === "" || cleaned[key] === undefined) delete cleaned[key];
        });
        save("specifications", cleaned, "Specifications saved");
    };

    const savePerformance = () => save("performance", performance, "Performance details saved");

    const saveColors = () => {
        const cleaned = colors.filter((c) => c.name.trim() || c.hex.trim() || c.image.trim());
        save("colors", cleaned.map((c) => ({ name: c.name.trim(), hex: c.hex.trim(), image: c.image.trim() })), "Colors saved");
    };

    const saveImages = () => {
        const cleaned = images.filter((i) => i.url.trim() || i.alt.trim() || i.title.trim());
        const invalid = cleaned.some((i) => !i.url.trim());
        if (invalid) {
            toast.error("Every image needs a url");
            return;
        }
        save("images", cleaned.map((i) => ({ url: i.url.trim(), alt: i.alt.trim(), title: i.title.trim(), type: i.type })), "Images saved");
    };

    const saveContentSections = () => {
        const cleaned = contentSections.filter((c) => c.heading.trim() || c.content.trim());
        save(
            "contentSections",
            cleaned.map((c) => ({ heading: c.heading.trim(), slug: c.slug.trim(), content: c.content.trim(), ...(String(c.order).trim() !== "" ? { order: Number(c.order) } : {}) })),
            "Content sections saved",
        );
    };

    const updateRow = (setList, index, field, value) => setList((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    const addRow = (setList, factory) => setList((prev) => [...prev, factory()]);
    const removeRow = (setList, index) => setList((prev) => prev.filter((_, i) => i !== index));

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={200} />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <button type="button" className="btn back-btn text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(`/model/${modelId}`)}>
                <FaArrowLeft /> Back to {modelName || "model"}
            </button>

            <div className="mb-3">
                <h4 className="fw-semibold mb-1">Site Data - {modelName}</h4>
                <p className="text-muted mb-0">Optional marketing/CMS content for this model's public page. Each tab saves independently.</p>
            </div>

            <div className="settings-layout">
                <div className="settings-tabs" role="tablist" aria-orientation="vertical">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={`settings-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <span className="settings-tab-icon">{tab.icon}</span>
                            {tab.name}
                        </button>
                    ))}
                </div>

                <div className="settings-tab-panel">
                    {activeTab === "seo" ? (
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label">Title</label>
                                <input type="text" className="form-control" value={seo.title} onChange={(e) => setSeo({ ...seo, title: e.target.value })} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Canonical URL</label>
                                <input type="text" className="form-control" value={seo.canonicalUrl} onChange={(e) => setSeo({ ...seo, canonicalUrl: e.target.value })} />
                            </div>
                            <div className="col-12">
                                <label className="form-label">Description</label>
                                <textarea className="form-control" rows={2} value={seo.description} onChange={(e) => setSeo({ ...seo, description: e.target.value })} />
                            </div>
                            <ChipListEditor label="Keywords" values={seo.keywords} onChange={(v) => setSeo({ ...seo, keywords: v })} placeholder="Type a keyword and press Enter" />
                            <div className="col-12 col-md-6">
                                <label className="form-label">OG title</label>
                                <input type="text" className="form-control" value={seo.ogTitle} onChange={(e) => setSeo({ ...seo, ogTitle: e.target.value })} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">OG image URL</label>
                                <input type="text" className="form-control" value={seo.ogImage} onChange={(e) => setSeo({ ...seo, ogImage: e.target.value })} />
                            </div>
                            <div className="col-12">
                                <label className="form-label">OG description</label>
                                <textarea className="form-control" rows={2} value={seo.ogDescription} onChange={(e) => setSeo({ ...seo, ogDescription: e.target.value })} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Robots</label>
                                <input type="text" className="form-control" value={seo.robots} onChange={(e) => setSeo({ ...seo, robots: e.target.value })} placeholder="index, follow" />
                            </div>
                            <div className="col-12 d-flex justify-content-end">
                                <button type="button" className="btn btn-primary" onClick={saveSeo} disabled={saving || !canSave} hidden={!canSave}>{saving ? "Saving..." : "Save SEO"}</button>
                            </div>
                        </div>
                    ) : null}

                    {activeTab === "overview" ? (
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label">Short description</label>
                                <textarea className="form-control" rows={2} value={overview.shortDescription} onChange={(e) => setOverview({ ...overview, shortDescription: e.target.value })} />
                            </div>
                            <div className="col-12">
                                <label className="form-label">Description</label>
                                <textarea className="form-control" rows={4} value={overview.description} onChange={(e) => setOverview({ ...overview, description: e.target.value })} />
                            </div>
                            <ChipListEditor label="Highlights" values={overview.highlights} onChange={(v) => setOverview({ ...overview, highlights: v })} placeholder="Type a highlight and press Enter" />
                            <div className="col-12 d-flex justify-content-end">
                                <button type="button" className="btn btn-primary" onClick={saveOverview} disabled={saving || !canSave} hidden={!canSave}>{saving ? "Saving..." : "Save overview"}</button>
                            </div>
                        </div>
                    ) : null}

                    {activeTab === "specifications" ? (
                        <div className="row g-3">
                            {[
                                ["engine", "Engine"], ["engineDisplacement", "Engine displacement"], ["engineType", "Engine type"],
                                ["cylinders", "Cylinders"], ["maxPower", "Max power"], ["maxTorque", "Max torque"],
                                ["transmission", "Transmission"], ["driveType", "Drive type"], ["fuelType", "Fuel type"],
                                ["mileage", "Mileage"], ["electricRange", "Electric range"], ["batteryCapacity", "Battery capacity"],
                                ["chargingTime", "Charging time"], ["seatingCapacity", "Seating capacity"], ["bootSpace", "Boot space"],
                                ["fuelTankCapacity", "Fuel tank capacity"], ["groundClearance", "Ground clearance"], ["length", "Length"],
                                ["width", "Width"], ["height", "Height"], ["wheelbase", "Wheelbase"], ["kerbWeight", "Kerb weight"],
                            ].map(([field, label]) => (
                                <div key={field} className="col-12 col-md-4">
                                    <label className="form-label">{label}</label>
                                    <input
                                        type={field === "cylinders" || field === "seatingCapacity" ? "number" : "text"}
                                        className="form-control"
                                        value={specifications[field]}
                                        onChange={(e) => setSpecifications({ ...specifications, [field]: e.target.value })}
                                    />
                                </div>
                            ))}
                            <div className="col-12 d-flex justify-content-end">
                                <button type="button" className="btn btn-primary" onClick={saveSpecifications} disabled={saving || !canSave} hidden={!canSave}>{saving ? "Saving..." : "Save specifications"}</button>
                            </div>
                        </div>
                    ) : null}

                    {activeTab === "performance" ? (
                        <div className="row g-3">
                            <div className="col-12 col-md-4">
                                <label className="form-label">Top speed</label>
                                <input type="text" className="form-control" value={performance.topSpeed} onChange={(e) => setPerformance({ ...performance, topSpeed: e.target.value })} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Acceleration</label>
                                <input type="text" className="form-control" value={performance.acceleration} onChange={(e) => setPerformance({ ...performance, acceleration: e.target.value })} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">0-100</label>
                                <input type="text" className="form-control" value={performance.zeroToHundred} onChange={(e) => setPerformance({ ...performance, zeroToHundred: e.target.value })} />
                            </div>
                            <ChipListEditor label="Driving modes" values={performance.drivingModes} onChange={(v) => setPerformance({ ...performance, drivingModes: v })} placeholder="Type a driving mode and press Enter" />
                            <div className="col-12 d-flex justify-content-end">
                                <button type="button" className="btn btn-primary" onClick={savePerformance} disabled={saving || !canSave} hidden={!canSave}>{saving ? "Saving..." : "Save performance"}</button>
                            </div>
                        </div>
                    ) : null}

                    {activeTab === "colors" ? (
                        <div>
                            {colors.map((row, index) => (
                                <div key={index} className="row g-2 align-items-center mb-2">
                                    <div className="col-12 col-md-4">
                                        <input type="text" className="form-control" placeholder="Color name" value={row.name} onChange={(e) => updateRow(setColors, index, "name", e.target.value)} />
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <input type="text" className="form-control" placeholder="#RRGGBB" value={row.hex} onChange={(e) => updateRow(setColors, index, "hex", e.target.value)} />
                                    </div>
                                    <div className="col-6 col-md-4">
                                        <input type="text" className="form-control" placeholder="Image URL" value={row.image} onChange={(e) => updateRow(setColors, index, "image", e.target.value)} />
                                    </div>
                                    <div className="col-12 col-md-1">
                                        {colors.length > 1 ? (
                                            <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeRow(setColors, index)}>&times;</button>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                            <div className="d-flex justify-content-between mt-2">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addRow(setColors, emptyColorRow)}>+ Add color</button>
                                <button type="button" className="btn btn-primary" onClick={saveColors} disabled={saving || !canSave} hidden={!canSave}>{saving ? "Saving..." : "Save colors"}</button>
                            </div>
                        </div>
                    ) : null}

                    {activeTab === "images" ? (
                        <div>
                            {images.map((row, index) => (
                                <div key={index} className="row g-2 align-items-center mb-2">
                                    <div className="col-12 col-md-4">
                                        <input type="text" className="form-control" placeholder="Image URL" value={row.url} onChange={(e) => updateRow(setImages, index, "url", e.target.value)} />
                                    </div>
                                    <div className="col-6 col-md-2">
                                        <input type="text" className="form-control" placeholder="Alt text" value={row.alt} onChange={(e) => updateRow(setImages, index, "alt", e.target.value)} />
                                    </div>
                                    <div className="col-6 col-md-2">
                                        <input type="text" className="form-control" placeholder="Title" value={row.title} onChange={(e) => updateRow(setImages, index, "title", e.target.value)} />
                                    </div>
                                    <div className="col-8 col-md-3">
                                        <select className="form-select" value={row.type} onChange={(e) => updateRow(setImages, index, "type", e.target.value)}>
                                            {SITE_IMAGE_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                                        </select>
                                    </div>
                                    <div className="col-4 col-md-1">
                                        {images.length > 1 ? (
                                            <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeRow(setImages, index)}>&times;</button>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                            <div className="d-flex justify-content-between mt-2">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addRow(setImages, emptyImageRow)}>+ Add image</button>
                                <button type="button" className="btn btn-primary" onClick={saveImages} disabled={saving || !canSave} hidden={!canSave}>{saving ? "Saving..." : "Save images"}</button>
                            </div>
                        </div>
                    ) : null}

                    {activeTab === "content" ? (
                        <div>
                            {contentSections.map((row, index) => (
                                <div key={index} className="row g-2 align-items-start mb-3 pb-3 border-bottom">
                                    <div className="col-12 col-md-5">
                                        <label className="form-label">Heading</label>
                                        <input type="text" className="form-control" value={row.heading} onChange={(e) => updateRow(setContentSections, index, "heading", e.target.value)} />
                                    </div>
                                    <div className="col-8 col-md-4">
                                        <label className="form-label">Slug</label>
                                        <input type="text" className="form-control" value={row.slug} onChange={(e) => updateRow(setContentSections, index, "slug", e.target.value)} />
                                    </div>
                                    <div className="col-4 col-md-2">
                                        <label className="form-label">Order</label>
                                        <input type="number" className="form-control" value={row.order} onChange={(e) => updateRow(setContentSections, index, "order", e.target.value)} />
                                    </div>
                                    <div className="col-12 col-md-1 d-flex align-items-end">
                                        {contentSections.length > 1 ? (
                                            <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeRow(setContentSections, index)}>&times;</button>
                                        ) : null}
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Content</label>
                                        <textarea className="form-control" rows={3} value={row.content} onChange={(e) => updateRow(setContentSections, index, "content", e.target.value)} />
                                    </div>
                                </div>
                            ))}
                            <div className="d-flex justify-content-between mt-2">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addRow(setContentSections, emptyContentRow)}>+ Add section</button>
                                <button type="button" className="btn btn-primary" onClick={saveContentSections} disabled={saving || !canSave} hidden={!canSave}>{saving ? "Saving..." : "Save content sections"}</button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default ModelSiteDataPage;
